import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { getBiddingInfo } from '@/redux/modules/biddingInfo';
import { getCatalogRegistration } from '@/redux/modules/catalogRegistration';
import { getItem } from '@/redux/modules/item';
import { getUserIsInGoodStanding } from '@/redux/modules/user';
import isEqual from 'react-fast-compare';
import styled from 'styled-components';
import { useSelector } from 'react-redux';

type Props = {
    itemId: number;
};

const CardStatusBanner = ({ itemId }: Props) => {
    let banner = null;
    let bannerOverlay = '';

    const { catalogId } = useSelector((state) => getItem(state, itemId), isEqual);
    const accountInGoodStanding = useSelector(getUserIsInGoodStanding);

    const { bidderHasBid, bidderHasHighBid, isAvailable, isPassed, isReserveMet, isSold } = useSelector(
        (state) => getBiddingInfo(state, itemId),
        isEqual
    );

    const { blockedByAuctioneer, pending, suspended } = useSelector(
        (state) => getCatalogRegistration(state, catalogId),
        isEqual
    );

    if (blockedByAuctioneer || suspended || !accountInGoodStanding) {
        banner = <FormattedMessage id="account_suspended" />;
        bannerOverlay = 'shadowOverlay400';
    } else if (isAvailable) {
        if (bidderHasHighBid && isReserveMet) {
            if (pending) {
                banner = <FormattedMessage id="registration_pending" />;
                bannerOverlay = 'shadowOverlay400';
            } else {
                banner = <FormattedMessage id="you_are_leading" />;
                bannerOverlay = 'shadowOverlayGreen100';
            }
        } else if (bidderHasHighBid && !isReserveMet) {
            banner = <FormattedMessage id="reserve_not_met" />;
            bannerOverlay = 'shadowOverlayRed200';
        } else if (bidderHasBid && !bidderHasHighBid) {
            banner = <FormattedMessage id="outbid" />;
            bannerOverlay = 'shadowOverlayRed200';
        }
    } else if (bidderHasBid && bidderHasHighBid && isSold) {
        banner = <FormattedMessage id="you_won" />;
        bannerOverlay = 'shadowOverlayGreen100';
    } else if (isPassed) {
        if (pending) {
            banner = <FormattedMessage id="not_approved" />;
            bannerOverlay = 'shadowOverlay400';
        } else {
            banner = <FormattedMessage id="lot_passed" />;
            bannerOverlay = 'shadowOverlayRed200';
        }
    } else if (bidderHasBid) {
        banner = <FormattedMessage id="lost" />;
        bannerOverlay = 'shadowOverlayRed200';
    }

    return !bidderHasBid && !isAvailable ? null : <BannerDiv overlay={bannerOverlay}>{banner}</BannerDiv>;
};

export default React.memo<Props>(CardStatusBanner);

const BannerDiv = styled.div<{ overlay }>`
    display: ${({ overlay }) => (overlay ? 'block' : 'none')};
    position: absolute;
    width: 100%;
    height: 22px;
    line-height: 22px;
    bottom: 20px;
    right: 0;
    left: 0;
    font-family: ${({ theme }) => theme.typography.fontSecondary};
    font-size: 12px;
    color: ${({ theme }) => theme.colors.white};
    box-shadow: 0 0 8px 0 ${({ theme }) => theme.colors.shadow200};
    cursor: default;
    user-select: none;
    letter-spacing: 0.5px;
    background-color: ${({ overlay, theme }) => (overlay ? theme.colors[overlay] : 'transparent')};

    @media print {
        display: none !important;
    }
`;
